import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { User } from '../../types';

interface Props {
  users: User[];                              // users éligibles (même tenant, sauf moi)
  onClose: () => void;
  onSelectUser: (userId: string) => void;     // démarre une conv directe avec ce user
  onCreateGroup: (name: string, participantIds: string[], isPrivate: boolean) => Promise<void> | void;
}

type Tab = 'direct' | 'group';

const NewConversationModal: React.FC<Props> = ({ users, onClose, onSelectUser, onCreateGroup }) => {
  const [tab, setTab] = useState<Tab>('direct');
  const [search, setSearch] = useState('');

  // État spécifique au flow groupe
  const [groupName, setGroupName] = useState('');
  const [groupIsPrivate, setGroupIsPrivate] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      u.name.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const toggleSelected = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleCreateGroup = async () => {
    setError(null);
    const name = groupName.trim();
    if (!name) {
      setError('Le nom du groupe est requis');
      return;
    }
    if (selectedIds.size < 1) {
      setError('Sélectionnez au moins 1 autre participant');
      return;
    }
    setCreating(true);
    try {
      await onCreateGroup(name, Array.from(selectedIds), groupIsPrivate);
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const modal = (
    <div className="orion-modal-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="orion-modal-panel w-full max-w-md rounded-[2rem] shadow-2xl border h-[640px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-200 dark:border-[rgba(100,140,210,0.2)] px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900 dark:text-[#e0eaff]">Nouvelle conversation</h3>
          <button type="button" onClick={onClose} className="text-slate-500 dark:text-[#546a96] hover:text-slate-900 dark:hover:text-[#c8daf5] p-1" aria-label="Fermer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Onglets */}
        <div className="border-b border-slate-100 dark:border-[rgba(100,140,210,0.1)] flex">
          <button
            type="button"
            onClick={() => setTab('direct')}
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
              tab === 'direct' ? 'text-cyan-600 dark:text-cyan-300 border-b-2 border-cyan-500 dark:border-cyan-400' : 'text-slate-500 dark:text-[#7f93bc] hover:text-slate-900 dark:hover:text-[#c8daf5]'
            }`}
          >
            Direct (1-to-1)
          </button>
          <button
            type="button"
            onClick={() => setTab('group')}
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors ${
              tab === 'group' ? 'text-cyan-600 dark:text-cyan-300 border-b-2 border-cyan-500 dark:border-cyan-400' : 'text-slate-500 dark:text-[#7f93bc] hover:text-slate-900 dark:hover:text-[#c8daf5]'
            }`}
          >
            Groupe
          </button>
        </div>

        {/* Inputs spécifiques groupe */}
        {tab === 'group' && (
          <div className="px-4 pt-3 pb-2 border-b border-slate-100 dark:border-[rgba(100,140,210,0.1)] space-y-2">
            <input
              type="text"
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              maxLength={200}
              placeholder="Nom du groupe (ex: Équipe commerciale)"
              className="orion-settings-input w-full rounded-xl px-3 py-2 text-sm"
            />
            <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-700 dark:text-[#c8daf5]">
              <input
                type="checkbox"
                checked={groupIsPrivate}
                onChange={e => setGroupIsPrivate(e.target.checked)}
                className="w-4 h-4 accent-cyan-500"
              />
              <span>
                🔒 <strong>Groupe privé</strong> — pas d'observation par les managers
              </span>
            </label>
          </div>
        )}

        {/* Recherche */}
        <div className="p-4 border-b border-slate-100 dark:border-[rgba(100,140,210,0.1)]">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un collègue par nom ou email…"
            className="orion-settings-input w-full rounded-xl px-3 py-2 text-sm"
            autoFocus
          />
          {tab === 'group' && selectedIds.size > 0 && (
            <p className="text-[10px] text-cyan-600 dark:text-cyan-300 mt-2">
              {selectedIds.size} participant{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Liste des users */}
        <div className="flex-1 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <div className="text-center text-xs text-slate-500 dark:text-[#546a96] py-8">
              Aucun utilisateur trouvé.
            </div>
          ) : (
            filtered.map(u => {
              const isSelected = selectedIds.has(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    if (tab === 'direct') {
                      onSelectUser(u.id);
                    } else {
                      toggleSelected(u.id);
                    }
                  }}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
                    isSelected && tab === 'group'
                      ? 'bg-cyan-50 dark:bg-cyan-500/15 border border-cyan-300 dark:border-cyan-500/40'
                      : 'hover:bg-slate-100 dark:hover:bg-[rgba(100,140,210,0.1)] border border-transparent'
                  }`}
                >
                  <img
                    src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${u.email || u.id}`}
                    alt=""
                    className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-[rgba(15,30,60,0.6)] border border-slate-200 dark:border-[rgba(100,140,210,0.2)]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900 dark:text-[#e0eaff] truncate">{u.name}</div>
                    <div className="text-[10px] text-slate-500 dark:text-[#7f93bc] truncate">{u.email}</div>
                  </div>
                  {tab === 'group' && isSelected && (
                    <span className="text-cyan-600 dark:text-cyan-300 text-base">✓</span>
                  )}
                  <span className="text-[9px] text-slate-400 dark:text-[#546a96] font-mono">{u.role}</span>
                </button>
              );
            })
          )}
        </div>

        {/* Bouton créer le groupe */}
        {tab === 'group' && (
          <div className="border-t border-slate-200 dark:border-[rgba(100,140,210,0.2)] px-4 py-3 space-y-2">
            {error && (
              <div className="text-[11px] font-bold text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 rounded-md px-2 py-1">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={handleCreateGroup}
              disabled={creating || !groupName.trim() || selectedIds.size < 1}
              className="orion-settings-primary-btn w-full text-white font-bold py-2 rounded-xl text-sm disabled:opacity-50"
            >
              {creating ? 'Création…' : `Créer le groupe (${selectedIds.size + 1} membres)`}
            </button>
          </div>
        )}

        {tab === 'direct' && (
          <div className="border-t border-slate-200 dark:border-[rgba(100,140,210,0.2)] px-6 py-3 text-[10px] text-slate-500 dark:text-[#546a96]">
            💡 Sélectionnez un collègue pour démarrer une discussion privée.
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default NewConversationModal;
